import React, { useEffect, useState } from "react";
import Typography from "style-guide/component/Typography";
import Modal from "style-guide/component/Modal";
import Button from "style-guide/component/Button";
import { MoreOutlined, PlusOutlined, CreditCardOutlined } from "@ant-design/icons";
import { Radio, Dropdown } from "antd";
import { deletePaymentMethod, getPaymentMethods, setDefaultPaymentMethod } from "../../../redux/slice/rfpModuleSlice/subscription";
import { AppDispatch, RootState } from "redux/store";
import { useDispatch, useSelector } from "react-redux";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import StripeWrapper from "components/payment/StripeWrapper";

interface LoadingStates {
  fetching: boolean;
  adding: boolean;
  deleting: boolean;
  settingDefault: boolean;
}

interface ModalStates {
  addPayment: boolean;
  confirmDelete: string | number | null;
  confirmDefault: string | number | null;
}

const PaymentMethodSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div className="w-4 h-4 bg-gray-300 rounded-full" />
        <div className="w-12 h-8 bg-gray-300 rounded" />
        <div className="flex flex-col space-y-2">
          <div className="w-32 h-4 bg-gray-300 rounded" />
          <div className="w-24 h-3 bg-gray-300 rounded" />
        </div>
      </div>
      <div className="w-6 h-6 bg-gray-300 rounded" />
    </div>
  </div>
);

const EmptyPaymentMethods = ({ onAddPaymentMethod }: { onAddPaymentMethod: () => void }) => (
  <div className="text-center py-12">
    <div className="flex justify-center mb-4">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
        <CreditCardOutlined className="text-2xl text-gray-400" />
      </div>
    </div>
    <Typography variant="Heading 5" text="No Payment Methods" className="mb-2" />
    <Typography 
      variant="Base" 
      text="You haven't added any payment methods yet. Add one to get started." 
      className="text-gray-500 mb-6"
    />
    <Button
      type="Default"
      buttonText="Add Payment Method"
      onClick={onAddPaymentMethod}
      icon
      iconComponent={<PlusOutlined className="text-sm" />}
      className="text-blue-600 border-none bg-transparent hover:bg-blue-50 p-0" />
  </div>
);

const PaymentMethods = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [selectedMethodId, setSelectedMethodId] = useState<string>("");
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    fetching: false,
    adding: false,
    deleting: false,
    settingDefault: false,
  });
  const [modalStates, setModalStates] = useState<ModalStates>({
    addPayment: false,
    confirmDelete: null,
    confirmDefault: null,
  });

  const { paymentMethodsData } = useSelector(
    (state: RootState) => state.subscriptionData
  );

  const updateLoadingState = (key: keyof LoadingStates, value: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const updateModalState = (key: keyof ModalStates, value: boolean | string | number | null) => {
    setModalStates(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      updateLoadingState('fetching', true);
      try {
        await dispatch(getPaymentMethods());
      } finally {
        updateLoadingState('fetching', false);
      }
    };
    fetchPaymentMethods();
  }, [dispatch]);

  useEffect(() => {
    if (paymentMethodsData?.data?.length > 0 && !selectedMethodId) {
      setSelectedMethodId(paymentMethodsData.data[0].id);
    }
  }, [paymentMethodsData, selectedMethodId]);

  const handleSelectPaymentMethod = (methodId: string) => {
    setSelectedMethodId(methodId);
    updateModalState('confirmDefault', methodId);
  };

  const handleAddPaymentMethod = () => {
    updateModalState('addPayment', true);
  };

  const handleCloseModal = () => {
    updateModalState('addPayment', false);
  };

  const handleSavePaymentMethod = async () => {
    updateModalState('addPayment', false);
    updateLoadingState('adding', false);
    updateLoadingState('fetching', true);
    await dispatch(getPaymentMethods());
    updateLoadingState('fetching', false);
  };

  const handleDeletePaymentMethod = (id: string | number) => {
    updateModalState('confirmDelete', id);
  };

  const confirmDelete = async () => {
    if (modalStates.confirmDelete !== null) {
      updateLoadingState('deleting', true);
      try {
        await dispatch(deletePaymentMethod(modalStates.confirmDelete));
        updateModalState('confirmDelete', null);
      } finally {
        updateLoadingState('deleting', false);
      }
    }
  };

  const cancelDelete = () => updateModalState('confirmDelete', null);

  const confirmSetDefault = async () => {
    if (modalStates.confirmDefault !== null) {
      updateLoadingState('settingDefault', true);
      try {
        await dispatch(setDefaultPaymentMethod(modalStates.confirmDefault as string));
        updateModalState('confirmDefault', null);
      } finally {
        updateLoadingState('settingDefault', false);
      }
    }
  };

  const cancelSetDefault = () => updateModalState('confirmDefault', null);

  const getCardIcon = (type: string) => {
    switch (type) {
      case "visa":
        return "VISA";
      case "mastercard":
        return "MC";
      case "amex":
        return "AMEX";
      default:
        return "CARD";
    }
  };

  const getMenuItems = (id: string | number) => [
    {
      key: "delete",
      label: "Delete Method",
      onClick: () => handleDeletePaymentMethod(id),
      style: { color: "#ff4d4f" },
    },
  ];

  if (loadingStates.fetching) {
    return (
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <Typography variant="Heading 4" text="Payment Methods" />
        </div>

        {/* Loading Skeletons */}
        <div className="space-y-4">
          <PaymentMethodSkeleton />
          <PaymentMethodSkeleton />
        </div>

        {/* Add Payment Method Button Skeleton */}
        <div className="mt-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const hasNoData = !paymentMethodsData?.data || paymentMethodsData.data.length === 0;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="Heading 4" text="Payment Methods" />
      </div>

      {hasNoData ? (
        <EmptyPaymentMethods onAddPaymentMethod={handleAddPaymentMethod} />
      ) : (
        <>
          {/* Payment Methods List */}
          <div className="space-y-4">
            {paymentMethodsData.data.map((method) => (
              <div
                key={method?.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
              >
                {/* Left side - Radio button and card info */}
                <div className="flex items-center space-x-4">
                  <Radio
                    checked={method.isDefaultMethod}
                    onChange={() => handleSelectPaymentMethod(method.id)}
                    className="text-blue-600"
                  />

                  {/* Card Icon */}
                  <div className="w-12 h-8 bg-white text-blue-primary border-2 border-gray-200 rounded flex items-center justify-center text-xs font-bold">
                    {getCardIcon(method?.brand)}
                  </div>

                  {/* Card Details */}
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2">
                      <Typography
                        variant="Base"
                        text={`•••• •••• •••• ${method?.last4}`}
                        className="font-medium"
                      />
                    </div>
                    <Typography
                      variant="SM"
                      text={`Expiry ${method?.expMonth}/${method?.expYear}`}
                      className="text-gray-500"
                    />
                  </div>
                </div>

                {/* Right side - More options */}
                <Dropdown
                  menu={{ items: getMenuItems(method.id) }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <MoreOutlined className="text-lg text-black" />
                </Dropdown>
              </div>
            ))}
          </div>

          {/* Add Payment Method Button */}
          <div className="mt-6">
            <Button
              type="Default"
              buttonText="Add Payment Method"
              onClick={handleAddPaymentMethod}
              size="Medium"
              icon
              className="text-blue-600 border-none bg-transparent hover:bg-blue-50 p-0"
              iconComponent={<PlusOutlined className="text-sm" />}
            />
          </div>
        </>
      )}

      {/* Add Payment Method Modal */}
      <StripeWrapper>
        <AddPaymentMethodModal
          isOpen={modalStates.addPayment}
          onClose={handleCloseModal}
          onSave={handleSavePaymentMethod}
          isLoading={loadingStates.adding}
          setIsAddingPayment={(value) => updateLoadingState('adding', value)}
        />
      </StripeWrapper>

      {/* Confirm Delete Modal */}
      <Modal
        isOpen={modalStates.confirmDelete !== null}
        onClose={loadingStates.deleting ? () => {} : cancelDelete}
        title="Delete Payment Method"
        backdropClosable={!loadingStates.deleting}
        showCloseIcon={!loadingStates.deleting}
        footer={
          <>
            <Button 
              type={loadingStates.deleting ? "Disabled" : "Default"} 
              onClick={cancelDelete} 
              buttonText="No" 
            />
            <Button 
              type="Danger" 
              onClick={confirmDelete} 
              buttonText={loadingStates.deleting ? "Deleting..." : "Yes, Delete"} 
              className="text-white bg-red-500"
              loading={loadingStates.deleting}
            />
          </>
        }
      >
        <Typography variant="Base" className="mb-2">
          Are you sure you want to delete this payment method?
        </Typography>
      </Modal>

      {/* Confirm Set Default Modal */}
      <Modal
        isOpen={modalStates.confirmDefault !== null}
        onClose={loadingStates.settingDefault ? () => {} : cancelSetDefault}
        title="Set Default Payment Method"
        backdropClosable={!loadingStates.settingDefault}
        showCloseIcon={!loadingStates.settingDefault}
        footer={
          <>
            <Button 
              type={loadingStates.settingDefault ? "Disabled" : "Default"} 
              onClick={cancelSetDefault} 
              buttonText="No" 
            />
            <Button 
              type="Primary" 
              onClick={confirmSetDefault} 
              buttonText={loadingStates.settingDefault ? "Setting..." : "Yes, Set Default"}
              loading={loadingStates.settingDefault}
            />
          </>
        }
      >
        <Typography variant="Base" className="mb-2">
          Are you sure you want to mark this card as your default payment method?
        </Typography>
      </Modal>
    </div>
  );
};

export default PaymentMethods;
